import React, { useState, useEffect, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { createExternalExamGroupOperation } from '../operations/create-external-exam-group-operation';
import { updateExternalExamGroupOperation } from '../operations/update-external-exam-group-operation';
import { PaymentServiceSelector } from '../components/payment-service-selector';
import { observer } from 'mobx-react-lite';
import { AbstractExternalExam } from '../../../../../api/types/settings/studies/abstract-external-exam';
import { ExternalExamGroup } from '../../../../../api/types/external-exams/external-exam-group';
import {
  EditExamInFormDialog,
  type ExamInForm,
} from './compoenents/edit-exam-in-form-dialog';
import { SelectedExamsList } from './compoenents/selected-exams-list';
import { SelectExamDialog } from './compoenents/select-exam-dialog';
import {
  CreateExternalExamGroupExamRequest,
  CreateExternalExamGroupRequest,
} from '../../../../../api/types/external-exams/external-exam-group-crud';
import { EditText } from '../../../../../component/ui/edit-text/edit-text';
import globalSettingsStores from '../../../../../stores/settings-stores';
import { EditNumber } from '../../../../../component/ui/edit-text/edit-number';
import CancelButton from '../../../../../component/ui/buttons/cancel-button/cancel-button';
import ApproveButtons from '../../../../../component/ui/buttons/approve-button/approve-buttons';
import { Dropdown } from 'primereact/dropdown';
import globalSettingsBranches from '../../../../../stores/global-settings-branches';
import { getHebrewYear } from '../../../../../utils/hebrew-date-utils';
import { toast } from 'react-toastify';

interface AddEditGroupDialogProps {
  group: ExternalExamGroup | null; // null means create new
  visible: boolean;
  onHide: () => void;
}

export const AddEditGroupDialog: React.FC<AddEditGroupDialogProps> = observer(
  ({ group, visible, onHide }) => {
    const [groupName, setGroupName] = useState<string>('');
    const [year, setYear] = useState<number | null>(null);
    const [groupRegistrationDueDate, setGroupRegistrationDueDate] =
      useState<Date | null>(null);
    const [selectedExams, setSelectedExams] = useState<AbstractExternalExam[]>(
      [],
    );
    const [examsInForm, setExamsInForm] = useState<Map<number, ExamInForm>>(
      new Map(),
    );
    const [editingExamId, setEditingExamId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [showSelectExamDialog, setShowSelectExamDialog] = useState(false);

    // Generate years list - memoized to avoid recalculation on every render
    const yearsOptions = useMemo(() => {
      const availableYears =
        globalSettingsBranches.workingTimeManagement?.availableWorkingYears ||
        [];
      const yearsToUse =
        availableYears.length > 0
          ? availableYears
          : Array.from(
              { length: 10 },
              (_, i) => new Date().getFullYear() + i - 5,
            );

      return yearsToUse.map((yearValue) => ({
        label: `${getHebrewYear(yearValue)} (${yearValue} - ${yearValue + 1})`,
        value: yearValue,
      }));
    }, [globalSettingsBranches.workingTimeManagement?.availableWorkingYears]);

    useEffect(() => {
      if (group) {
        setGroupName(group.groupName);
        setYear(group.year);
        setGroupRegistrationDueDate(
          group.groupRegistrationDueDate
            ? new Date(group.groupRegistrationDueDate)
            : null,
        );
        const examMap = new Map<number, ExamInForm>();
        group.exams.forEach((exam) => {
          examMap.set(exam.abstractExternalExamsId, {
            externalExam: exam.abstractExternalExam,
            startAt: new Date(exam.startAt),
            durationMinutes: exam.durationMinutes,
            studentRegistrationDueDate: new Date(
              exam.studentRegistrationDueDate,
            ),
            paymentServiceId: exam.abstractExternalExam.paymentServiceId,
            paymentServiceVersionId: null, // Not used, kept for interface compatibility
          });
        });
        setExamsInForm(examMap);
        setSelectedExams(group.exams.map((e) => e.abstractExternalExam));
      } else {
        setGroupName('');
        setYear(
          globalSettingsBranches.workingTimeManagement?.currentWorkingYear ||
            new Date().getFullYear(),
        );
        setGroupRegistrationDueDate(null);
        setSelectedExams([]);
        setExamsInForm(new Map());
      }
    }, [group, visible]);

    const handleExamSelection = (exams: AbstractExternalExam[]) => {
      setSelectedExams(exams);
      const newMap = new Map(examsInForm);
      // Add new exams to the map
      exams.forEach((exam) => {
        if (!newMap.has(exam.id)) {
          newMap.set(exam.id, {
            externalExam: exam,
            startAt: null,
            durationMinutes: 90,
            studentRegistrationDueDate: groupRegistrationDueDate ?? null,
            paymentServiceId: exam.paymentServiceId, // Use payment service from abstract external exam
            paymentServiceVersionId: null, // Not used, kept for interface compatibility
          });
        }
      });
      setExamsInForm(newMap);
    };

    const handleAddExams = (newExams: AbstractExternalExam[]) => {
      const updatedSelectedExams = [...selectedExams, ...newExams];
      handleExamSelection(updatedSelectedExams);
    };

    const updateExamInForm = (examId: number, updates: Partial<ExamInForm>) => {
      const newMap = new Map(examsInForm);
      const existing = newMap.get(examId);
      if (existing) {
        newMap.set(examId, { ...existing, ...updates });
      }
      setExamsInForm(newMap);
    };

    const handleSave = async () => {
      if (!groupName.trim()) {
        return;
      }
      if (!year) {
        return;
      }
      if (!groupRegistrationDueDate) {
        toast.warn('יש לבחור תאריך אחרון להרשמה של סטודנטים');
        return;
      }

      // Validate that all exams have required fields
      for (const exam of examsInForm.values()) {
        if (!exam.paymentServiceId) {
          toast.warn(
            `למבחן ${exam.externalExam.examName} אין שירות תשלום מוגדר`,
          );
          return;
        }
      }

      const examsData: CreateExternalExamGroupExamRequest[] = Array.from(
        examsInForm.values(),
      ).map((exam) => ({
        abstractExternalExamsId: exam.externalExam.id,
        startAt: exam.startAt!,
        durationMinutes: exam.durationMinutes,
        studentRegistrationDueDate: exam.studentRegistrationDueDate!,
        paymentServiceId: exam.paymentServiceId!, // Use payment service from abstract external exam
      }));

      if (group) {
        // Update
        await updateExternalExamGroupOperation(
          group.id,
          {
            groupName: groupName.trim(),
            year: year,
            groupRegistrationDueDate: groupRegistrationDueDate,
          },
          setLoading,
          onHide,
        );
      } else {
        // Create
        const request: CreateExternalExamGroupRequest = {
          groupName: groupName.trim(),
          year: year,
          groupRegistrationDueDate: groupRegistrationDueDate,
          exams: examsData,
        };
        await createExternalExamGroupOperation(request, setLoading, onHide);
      }
    };

    const isValid =
      groupName.trim() &&
      year !== null &&
      groupRegistrationDueDate !== null &&
      examsInForm.size > 0 &&
      Array.from(examsInForm.values()).every(
        (exam) =>
          exam.startAt &&
          exam.durationMinutes > 0 &&
          exam.studentRegistrationDueDate &&
          exam.paymentServiceId,
      );

    const editingExam =
      editingExamId !== null ? examsInForm.get(editingExamId) : null;

    if (groupRegistrationDueDate && editingExam) {
      editingExam.studentRegistrationDueDate = groupRegistrationDueDate;
    }

    return (
      <Dialog
        header={group ? 'עריכת מקבץ' : 'הוספת מקבץ'}
        visible={visible}
        onHide={onHide}
        style={{
          width: '90vw',
          maxWidth: '1200px',
          backgroundColor: 'white',
          padding: '8px',
          borderRadius: '8px',
        }}
        modal
        closable={!loading}
      >
        <div style={{ direction: 'rtl', padding: '8px' }}>
          <EditText
            title="שם המקבץ"
            value={groupName}
            onChange={setGroupName}
          />

          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                fontSize: '14px',
                color: '#4b5563',
                marginBottom: '8px',
                display: 'block',
              }}
            >
              שנה אקדמית
            </label>
            <Dropdown
              value={year}
              onChange={(e) => setYear(e.value)}
              options={yearsOptions}
              optionLabel="label"
              optionValue="value"
              placeholder="בחר שנה אקדמית"
              style={{
                width: '100%',
                height: '36px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                fontSize: '14px',
                color: '#4b5563',
                marginBottom: '8px',
                display: 'block',
              }}
            >
              תאריך אחרון להרשמה של סטודנטים
            </label>
            <Calendar
              value={groupRegistrationDueDate}
              onChange={(e) => setGroupRegistrationDueDate(e.value as Date)}
              dateFormat="dd/mm/yy"
              style={{ width: '100%' }}
              inputStyle={{ direction: 'rtl', textAlign: 'right' }}
              panelStyle={{ direction: 'ltr' }}
            />
          </div>

          <SelectedExamsList
            examsInForm={examsInForm}
            onEditExam={(examId) => setEditingExamId(examId)}
            onAddExam={() => setShowSelectExamDialog(true)}
          />

          {examsInForm.size === 0 && (
            <div style={{ marginBottom: '16px' }}>
              <Button
                icon="pi pi-plus"
                label="הוספת מבחן"
                onClick={() => setShowSelectExamDialog(true)}
                style={{ height: '30px', fontSize: '14px' }}
              />
            </div>
          )}

          <SelectExamDialog
            visible={showSelectExamDialog}
            onHide={() => setShowSelectExamDialog(false)}
            onSelectedExams={handleAddExams}
            alreadySelectedExamIds={Array.from(examsInForm.keys())}
          />

          {editingExam && (
            <EditExamInFormDialog
              exam={editingExam}
              visible={editingExamId !== null}
              onHide={() => setEditingExamId(null)}
              onUpdate={(updates) => updateExamInForm(editingExamId!, updates)}
            />
          )}

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              marginTop: '20px',
            }}
          >
            <CancelButton onClick={onHide} disabled={loading} />
            <ApproveButtons
              onClick={handleSave}
              isLoading={loading}
              disabled={!isValid}
            />
          </div>
        </div>
      </Dialog>
    );
  },
);
